import type { OAuthCallbackResponse } from '@saaga/shared/interfaces/connector.interface'
import { useCompleteOAuthCallbackMutation } from '@saaga/shared/store/connector-endpoints.store'
import { useCallback, useRef } from 'react'

export interface UseOAuthCompleteReturn {
  handleCallback: (code: string, state: string) => Promise<void>
  handleError: (errorMessage: string) => void
  setProvider: (provider: string) => void
}

/**
 * Handles the completion phase of the OAuth flow — sending the callback
 * to the backend and dispatching success/error results.
 */
export const useOAuthComplete = (
  onSuccess?: (result: OAuthCallbackResponse) => void,
  onError?: (error: unknown) => void,
  onFinally?: () => void
): UseOAuthCompleteReturn => {
  const providerRef = useRef<string>('')
  const [completeOAuthCallback] = useCompleteOAuthCallbackMutation()

  const handleCallback = useCallback(
    async (code: string, state: string) => {
      onFinally?.()
      const result = await completeOAuthCallback({
        provider: providerRef.current,
        code,
        state,
      }).unwrap()
      onSuccess?.(result)
    },
    [completeOAuthCallback, onSuccess, onFinally]
  )

  const handleError = useCallback(
    (errorMessage: string) => {
      onFinally?.()
      onError?.(new Error(errorMessage))
    },
    [onError, onFinally]
  )

  const setProvider = useCallback((provider: string) => {
    providerRef.current = provider
  }, [])

  return { handleCallback, handleError, setProvider }
}
